/* ============================================================
   FAU load-more
   Appends the next page of products into the existing grid via
   the Section Rendering API. Custom element so it re-initialises
   automatically when Dawn re-renders the section after a filter
   or sort change.
   ============================================================ */
class FauLoadMore extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-load-more-button]');
    this.count = this.querySelector('[data-load-more-count]');
    this.fill = this.querySelector('[data-load-more-fill]');
    this.grid = document.getElementById('product-grid');
    this.loading = false;

    if (this.button) {
      this.button.addEventListener('click', this.onClick.bind(this));
    }
  }

  // paginate.next.url carries the whole query string forward, including the
  // section_id we appended. Left in place it compounds and breaks page 3.
  clean(url) {
    return url.replace(/([?&])section_id=[^&]*&?/g, '$1').replace(/[?&]$/, '');
  }

  async onClick(event) {
    if (this.loading) return;
    const nextUrl = this.button.dataset.nextUrl;
    if (!nextUrl || !this.grid) return; // no JS target: let the link navigate

    event.preventDefault();
    this.loading = true;
    this.button.setAttribute('aria-busy', 'true');
    const label = this.button.dataset.label || this.button.textContent.trim();
    this.button.textContent = this.button.dataset.loadingLabel || 'Loading...';

    const url = this.clean(nextUrl);
    const sep = url.indexOf('?') === -1 ? '?' : '&';

    try {
      const response = await fetch(url + sep + 'section_id=' + this.grid.dataset.id);
      if (!response.ok) throw new Error(response.status);

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const rows = Array.from(doc.querySelectorAll('#product-grid > li'));
      if (!rows.length) throw new Error('no products returned');

      const firstNew = rows[0];
      rows.forEach((row) => {
        // Dawn's scroll-reveal leaves appended items invisible - clear it.
        row.classList.remove('scroll-trigger', 'animate--slide-in');
        row.removeAttribute('data-cascade');
        row.style.removeProperty('--animation-order');
        this.grid.appendChild(row);
      });

      const freshCount = doc.querySelector('[data-load-more-count]');
      if (freshCount && this.count) this.count.innerHTML = freshCount.innerHTML;

      const freshFill = doc.querySelector('[data-load-more-fill]');
      if (freshFill && this.fill) this.fill.style.width = freshFill.style.width;

      const freshButton = doc.querySelector('[data-load-more-button]');
      if (freshButton) {
        this.button.dataset.nextUrl = freshButton.dataset.nextUrl;
        this.button.href = freshButton.getAttribute('href');
        this.button.textContent = label;
      } else {
        this.button.remove();
        this.button = null;
      }

      // Send focus and screen readers to the first product just added.
      const target = firstNew.querySelector('a, button');
      if (target) target.focus({ preventScroll: true });
    } catch (error) {
      console.error('[fau-load-more]', error);
      this.button.textContent = this.button.dataset.errorLabel || 'Something went wrong - tap to retry';
    } finally {
      this.loading = false;
      if (this.button) this.button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('fau-load-more')) {
  customElements.define('fau-load-more', FauLoadMore);
}
